"use client"

import { usePathname, useRouter } from 'next/navigation'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useKonamiCode } from '@/hooks/useKonamiCode'
import { trackEvent } from '@/lib/analytics'
import {
  playLawnSpaceSfx,
  unlockLawnSpaceAudio,
} from '@/lib/lawn-space-shooter/audio'

type Phase = 'idle' | 'flash' | 'rain' | 'announce' | 'wipe'

const BLADE_COUNT = 48
const BLADE_PALETTE = ['#8fff7f', '#6beb73', '#c4ffb6', '#3e8736', '#ffe56e']

type Blade = {
  id: number
  left: number
  delayMs: number
  durationMs: number
  rotation: number
  size: number
  hue: string
}

function generateBlades(): Blade[] {
  return Array.from({ length: BLADE_COUNT }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delayMs: Math.random() * 700,
    durationMs: 1100 + Math.random() * 1100,
    rotation: Math.random() * 360 - 180,
    size: 14 + Math.random() * 14,
    hue: BLADE_PALETTE[Math.floor(Math.random() * BLADE_PALETTE.length)] ?? '#8fff7f',
  }))
}

const STYLES = `
@keyframes konami-blade-fall {
  0% { transform: translate3d(0, -12vh, 0) rotate(var(--rot)); opacity: 0; }
  10% { opacity: 1; }
  100% { transform: translate3d(0, 112vh, 0) rotate(calc(var(--rot) + 540deg)); opacity: 1; }
}
@keyframes konami-flash {
  0% { background-color: #06120e; }
  20% { background-color: #ffffff; }
  40% { background-color: #ffe56e; }
  60% { background-color: #8fff7f; }
  80% { background-color: #234d20; }
  100% { background-color: #06120e; }
}
@keyframes konami-text-in {
  0% { opacity: 0; transform: scale(0.55); letter-spacing: 0.6em; }
  60% { opacity: 1; }
  100% { opacity: 1; transform: scale(1); letter-spacing: 0.18em; }
}
@keyframes konami-text-pulse {
  0%, 100% { transform: scale(1); text-shadow: 0 0 18px rgba(143,255,127,0.45), 0 6px 0 #234d20; }
  50% { transform: scale(1.06); text-shadow: 0 0 28px rgba(255,229,110,0.85), 0 6px 0 #234d20; }
}
@keyframes konami-scanlines {
  0% { background-position-y: 0; }
  100% { background-position-y: 8px; }
}
@keyframes konami-wipe {
  0% { clip-path: circle(0% at 50% 60%); }
  100% { clip-path: circle(180% at 50% 60%); }
}
`

export function KonamiGameLauncher() {
  const pathname = usePathname()
  const router = useRouter()
  const [phase, setPhase] = useState<Phase>('idle')
  const blades = useMemo(generateBlades, [])

  const isOnGameRoute = Boolean(pathname?.startsWith('/games/'))

  const activate = useCallback(() => {
    setPhase((current) => {
      if (current !== 'idle') return current
      unlockLawnSpaceAudio()
      trackEvent('konami_game_launched', { path: pathname ?? null })
      playLawnSpaceSfx('wave-clear')
      return 'flash'
    })
  }, [pathname])

  useKonamiCode(activate, !isOnGameRoute)

  const isLaunching = phase !== 'idle'

  useEffect(() => {
    if (!isLaunching) return
    let cancelled = false
    const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

    async function run() {
      await wait(350)
      if (cancelled) return
      setPhase('rain')

      await wait(400)
      if (cancelled) return
      playLawnSpaceSfx('powerup')
      await wait(500)
      if (cancelled) return
      setPhase('announce')

      await wait(200)
      if (cancelled) return
      playLawnSpaceSfx('destroy')
      await wait(600)
      if (cancelled) return
      setPhase('wipe')

      await wait(450)
      if (cancelled) return
      router.push('/games/lawn-space-shooter')
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [isLaunching, router])

  useEffect(() => {
    if (phase === 'idle') return
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setPhase('idle')
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [phase])

  if (phase === 'idle') return null

  return (
    <>
      <style>{STYLES}</style>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Konami code unlocked — launching Lawn Force One"
        data-testid="konami-launcher"
        className="fixed inset-0 z-[9999] overflow-hidden text-center"
        style={{
          backgroundColor: '#06120e',
          animation: phase === 'flash' ? 'konami-flash 350ms ease-out forwards' : undefined,
        }}
      >
        {phase !== 'wipe' && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 mix-blend-overlay opacity-25"
            style={{
              backgroundImage:
                'repeating-linear-gradient(0deg, rgba(0,0,0,0.7) 0, rgba(0,0,0,0.7) 2px, transparent 2px, transparent 4px)',
              animation: 'konami-scanlines 600ms linear infinite',
            }}
          />
        )}

        {(phase === 'rain' || phase === 'announce') && (
          <div aria-hidden="true" className="pointer-events-none absolute inset-0">
            {blades.map((blade) => (
              <div
                key={blade.id}
                className="absolute"
                style={{
                  left: `${blade.left}%`,
                  top: 0,
                  width: '4px',
                  height: `${blade.size}px`,
                  backgroundColor: blade.hue,
                  borderRadius: '2px',
                  boxShadow: `0 0 8px ${blade.hue}aa`,
                  ['--rot' as never]: `${blade.rotation}deg`,
                  animation: `konami-blade-fall ${blade.durationMs}ms linear ${blade.delayMs}ms infinite`,
                  willChange: 'transform, opacity',
                  transform: `rotate(${blade.rotation}deg)`,
                } as React.CSSProperties}
              />
            ))}
          </div>
        )}

        {(phase === 'flash' || phase === 'rain') && (
          <div className="relative z-10 flex h-full flex-col items-center justify-center px-6">
            <p
              className="font-mono text-xs uppercase text-[#8fff7f] sm:text-sm"
              style={{
                letterSpacing: '0.4em',
                animation: 'konami-text-in 320ms ease-out both',
              }}
            >
              ↑↑↓↓←→←→BA
            </p>
            <p
              className="mt-3 font-mono text-3xl font-black uppercase text-[#ffe56e] sm:text-5xl"
              style={{
                letterSpacing: '0.18em',
                animation:
                  'konami-text-in 420ms ease-out 120ms both, konami-text-pulse 900ms ease-in-out 460ms infinite',
              }}
            >
              Konami unlocked
            </p>
          </div>
        )}

        {phase === 'announce' && (
          <div className="relative z-10 flex h-full flex-col items-center justify-center px-6">
            <p
              className="font-mono text-xs uppercase text-[#79d7ff] sm:text-sm"
              style={{
                letterSpacing: '0.3em',
                animation: 'konami-text-in 220ms ease-out both',
              }}
            >
              Insert mower ▸
            </p>
            <h1
              className="mt-2 font-mono font-black uppercase text-[#ffe56e]"
              style={{
                fontSize: 'clamp(2.5rem, 9vw, 6.5rem)',
                lineHeight: '0.9',
                letterSpacing: '0.05em',
                animation:
                  'konami-text-in 360ms ease-out 80ms both, konami-text-pulse 720ms ease-in-out 420ms infinite',
              }}
            >
              Lawn Force One
            </h1>
            <p
              className="mt-5 font-mono text-sm uppercase text-[#8fff7f] sm:text-base"
              style={{
                letterSpacing: '0.22em',
                animation: 'konami-text-in 280ms ease-out 240ms both',
              }}
            >
              Launching arcade…
            </p>
          </div>
        )}

        {phase === 'wipe' && (
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              backgroundColor: '#8fff7f',
              animation: 'konami-wipe 450ms ease-out forwards',
            }}
          />
        )}

        <button
          type="button"
          onClick={() => setPhase('idle')}
          className="absolute right-4 top-4 z-20 rounded border border-[#8fff7f]/30 bg-[#06120e]/60 px-2.5 py-1 font-mono text-[0.65rem] uppercase text-[#8fff7f]/70 hover:border-[#ffe56e] hover:text-[#ffe56e]"
          style={{ letterSpacing: '0.2em' }}
        >
          Esc to cancel
        </button>
      </div>
    </>
  )
}
